import { useCallback, useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useTranslation } from 'react-i18next';
import { Plus, GripHorizontal, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/core/lib/utils';
import { getColorValue, getTextColorFromToken } from '@/core/lib/color-contrast';
import type { Task, ProjectStatus, TaskFilters } from '../../types';
import { KanbanColumnMenu } from './KanbanColumnMenu';
import { TaskCard } from './TaskCard';
import { InlineTaskCreator } from './InlineTaskCreator';
import { useTaskGroupInfinite } from '../../hooks/useTaskGroupInfinite';

interface KanbanColumnProps {
  projectId: string;
  boardId: string;
  filters: TaskFilters;
  status: ProjectStatus;
  onCreateTask: (title: string) => void;
  onTaskClick?: (task: Task) => void;
  onDelete?: (status: ProjectStatus) => void;
}

export function KanbanColumn({
  projectId,
  boardId,
  filters,
  status,
  onCreateTask,
  onTaskClick,
  onDelete,
}: KanbanColumnProps) {
  const { t } = useTranslation('tasks');
  const columnRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDraggingColumn, setIsDraggingColumn] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useTaskGroupInfinite({
      projectId,
      boardId,
      groupBy: 'status',
      groupKey: status.id,
      filters,
      enabled: isVisible,
    });

  const tasks = data?.pages.flatMap((p) => p.items) ?? [];
  const count = data?.pages[0]?.count ?? 0;
  const showLoader = !isVisible || isLoading;

  const colorValue = getColorValue(status.color);
  const textColor = getTextColorFromToken(status.color);
  const tasksBg = colorValue ? colorValue.replace(')', ' / 0.1)') : undefined;
  const tasksBgHover = colorValue ? colorValue.replace(')', ' / 0.2)') : undefined;

  const virtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 88,
    overscan: 3,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const wipLimit = status.wipLimit ?? null;
  const wipExceeded = wipLimit !== null && count > wipLimit;

  // Load the next page only in response to a real scroll near the bottom of the
  // column. Triggering from rendered virtual items instead fires on mount — before
  // any scroll exists — and cascades through every page at once. A scroll-driven
  // threshold matches Linear/ClickUp: page 1 loads on visibility, the rest on scroll.
  //
  // The ref is a synchronous guard: scroll fires dozens of events per gesture, and
  // React Query's isFetchingNextPage updates only after a re-render, so several
  // events would read the stale value and fire parallel fetchNextPage calls. The ref
  // flips immediately, so one gesture triggers exactly one page load.
  const loadingMoreRef = useRef(false);
  // Release the synchronous guard only once React Query confirms the fetch settled,
  // not when fetchNextPage's promise resolves (which can be a no-op that resolves
  // early and lets a new fetch slip through mid-gesture).
  useEffect(() => {
    if (!isFetchingNextPage) loadingMoreRef.current = false;
  }, [isFetchingNextPage]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasNextPage || isFetchingNextPage || loadingMoreRef.current) return;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceToBottom < 300) {
      loadingMoreRef.current = true;
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const el = columnRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = columnRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      getData: () => ({ statusId: status.id }),
      onDragEnter: () => setIsDragOver(true),
      onDragLeave: () => setIsDragOver(false),
      onDrop: () => setIsDragOver(false),
    });
  }, [status.id]);

  useEffect(() => {
    const el = columnRef.current;
    const handle = dragHandleRef.current;
    if (!el || !handle) return;

    return draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({
        type: 'column',
        statusId: status.id,
        columnStatusType: status.type,
      }),
      onDragStart: () => setIsDraggingColumn(true),
      onDrop: () => setIsDraggingColumn(false),
    });
  }, [status.id, status.type]);

  return (
    <div
      ref={columnRef}
      className={cn('flex h-full w-72 shrink-0 flex-col', isDraggingColumn && 'opacity-50')}
      data-testid={`tasks-kanban-column-${status.id}`}
    >
      {/* Column header */}
      <div
        className="group/col-header flex items-center justify-between rounded-t-lg px-2 py-2"
        style={{ backgroundColor: colorValue, color: textColor }}
        data-testid={`tasks-kanban-column-${status.id}-header`}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            ref={dragHandleRef}
            data-testid={`tasks-kanban-column-${status.id}-drag-handle`}
            className="cursor-grab touch-none opacity-50 hover:opacity-100 active:cursor-grabbing"
            aria-label={t('boardView.column.dragHandle')}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <GripHorizontal className="size-4" />
          </button>
          <span className="text-sm font-semibold">{status.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={`tasks-kanban-column-${status.id}-wip-count`}
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs',
                  wipExceeded
                    ? 'bg-destructive/80 text-destructive-foreground font-semibold'
                    : 'bg-white/20',
                )}
                aria-label={
                  wipExceeded
                    ? t('boardView.column.wipLimitExceeded')
                    : wipLimit !== null
                      ? t('boardView.column.wipLimitAria', { count: wipLimit })
                      : undefined
                }
              >
                {wipLimit !== null ? `${count}/${wipLimit}` : count}
              </span>
            </TooltipTrigger>
            {wipLimit !== null && (
              <TooltipContent>
                {wipExceeded
                  ? t('boardView.column.wipLimitExceeded')
                  : t('boardView.column.wipLimitAria', { count: wipLimit })}
              </TooltipContent>
            )}
          </Tooltip>
          <KanbanColumnMenu
            data-testid={`tasks-kanban-column-${status.id}-overflow`}
            status={status}
            onDelete={onDelete ? () => onDelete(status) : undefined}
          />
        </div>
      </div>

      {/* Column body */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col rounded-b-lg transition-colors',
          isDragOver && 'ring-2 ring-primary/30',
        )}
        style={{ backgroundColor: isDragOver ? tasksBgHover : tasksBg }}
      >
        {/* Scrollable tasks */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          data-testid={`tasks-kanban-column-${status.id}-task-list`}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto p-1"
        >
          {isError ? (
            <div className="flex h-16 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-destructive/40 text-xs text-muted-foreground">
              <span>{t('board.loadError')}</span>
              <button
                type="button"
                onClick={() => refetch()}
                className="font-medium text-primary hover:underline"
                data-testid={`tasks-kanban-column-${status.id}-retry`}
              >
                {t('retry')}
              </button>
            </div>
          ) : tasks.length === 0 && !isCreating ? (
            showLoader ? (
              <div className="flex h-16 items-center justify-center">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="flex h-16 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
                {t('board.empty')}
              </div>
            )
          ) : (
            <>
              <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
                {virtualItems.map((virtualItem) => (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualItem.start}px)`,
                      paddingBottom: '8px',
                    }}
                  >
                    <TaskCard
                      task={tasks[virtualItem.index]}
                      onClick={
                        onTaskClick ? () => onTaskClick(tasks[virtualItem.index]) : undefined
                      }
                      onEdit={() => {}}
                      onDelete={() => {}}
                    />
                  </div>
                ))}
              </div>
              {isFetchingNextPage && (
                <div className="flex justify-center py-2">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}
          {isCreating && (
            <InlineTaskCreator
              onConfirm={(title) => {
                onCreateTask(title);
                setIsCreating(false);
              }}
              onCancel={() => setIsCreating(false)}
            />
          )}
        </div>

        {/* Add task — fixed at bottom, never scrolls */}
        {!isCreating && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 justify-start gap-1.5 px-2"
            style={{ color: colorValue }}
            data-testid={`tasks-kanban-column-${status.id}-add-task`}
            onClick={() => setIsCreating(true)}
          >
            <Plus className="size-3.5" />
            {t('boardView.addTask')}
          </Button>
        )}
      </div>
    </div>
  );
}
